import { KeyRound, KeySquare, ShieldQuestion, Wrench } from 'lucide-react';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import type { AuthMode } from '@/types/api';

/**
 * Welche Anmeldewege stehen an einem System? (Migration 0040, 10.09.2026)
 *
 * Der Yubikey-Schlüssel ERSETZT das Tresor-Passwort nicht - er kommt als
 * zweiter Weg dazu, und der Client versucht ihn zuerst. Deshalb sagt diese
 * Marke immer BEIDE Wege in einem Wort und nie "Migration erledigt": eine
 * Beschriftung wie "auf Key umgestellt" würde behaupten, das Passwort sei
 * weg - und genau das soll es nicht sein.
 *
 * 'manuell' bekommt bewusst keinen Warnton. Es ist eine Entscheidung
 * (OPNsense, Windows, Appliance), kein Rückstand.
 */

interface Darstellung {
  label: string;
  tone: BadgeTone;
  titel: string;
}

const DARSTELLUNG: Record<AuthMode, Darstellung> = {
  passwort: {
    label: 'Nur Passwort',
    tone: 'warning',
    titel:
      'Der Zugang läuft über das Passwort im Bitwarden-Tresor. Die Sysadmin-Schlüssel ' +
      'sind hier noch nicht belegt - "Keys ausrollen" legt sie zusätzlich hin, das ' +
      'Passwort bleibt daneben bestehen.',
  },
  'passwort+key': {
    label: 'Passwort + Key',
    tone: 'success',
    titel:
      'Beide Wege stehen: das Passwort im Tresor und die Sysadmin-Schlüssel. Das ist ' +
      'der Zielzustand - der Client nimmt den Schlüssel und hat das Passwort als ' +
      'Rückfall.',
  },
  key: {
    label: 'Nur Key',
    tone: 'success',
    titel:
      'Die Sysadmin-Schlüssel sind belegt, eine Tresor-Verknüpfung gibt es an diesem ' +
      'System nicht (mehr).',
  },
  unbekannt: {
    label: 'Kein Weg bekannt',
    tone: 'danger',
    titel:
      'Weder eine Tresor-Verknüpfung noch ein belegter Schlüssel: nach dem heutigen ' +
      'Stand kommt hier niemand herein. Zuerst Schlüssel ausrollen oder das ' +
      'Tresor-Item nachtragen.',
  },
  manuell: {
    label: 'Handarbeit',
    tone: 'neutral',
    titel:
      'Dieses System ist ausdrücklich auf Handarbeit gestellt (OPNsense, Windows, ' +
      'Appliance). Es zählt nicht als Rückstand und wird von keiner Ausrollung ' +
      'angefasst.',
  },
};

function Symbol({ modus }: { modus: AuthMode }): JSX.Element {
  const klasse = 'size-3';
  if (modus === 'manuell') return <Wrench className={klasse} aria-hidden="true" />;
  if (modus === 'unbekannt') return <ShieldQuestion className={klasse} aria-hidden="true" />;
  if (modus === 'passwort') return <KeySquare className={klasse} aria-hidden="true" />;
  return <KeyRound className={klasse} aria-hidden="true" />;
}

interface AuthModeBadgeProps {
  /** null/undefined kommt von Altdaten - wird wie 'unbekannt' gezeigt. */
  authMode: AuthMode | null | undefined;
  className?: string;
  'data-testid'?: string;
}

export function AuthModeBadge({
  authMode,
  className,
  'data-testid': testId,
}: AuthModeBadgeProps): JSX.Element {
  // Fehlender Wert wird 'unbekannt' und NICHT 'passwort': ein System ohne
  // Angabe gehört auf die Arbeitsliste, nicht als erledigt gezeigt. Dieselbe
  // Regel wie im Read-Schema des Backends.
  const modus: AuthMode = authMode ?? 'unbekannt';
  const d = DARSTELLUNG[modus] ?? DARSTELLUNG.unbekannt;
  return (
    <Badge
      tone={d.tone}
      {...(className ? { className } : {})}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      <span className="inline-flex items-center gap-1" title={d.titel}>
        <Symbol modus={modus} />
        <span>{d.label}</span>
      </span>
    </Badge>
  );
}

/** Der Klartext ohne Marke - für Filter-Auswahllisten und Zusammenfassungen. */
export function authModeLabel(authMode: AuthMode | null | undefined): string {
  return (DARSTELLUNG[authMode ?? 'unbekannt'] ?? DARSTELLUNG.unbekannt).label;
}

/**
 * Woher der Beleg kam, in Worten. `key_verified_by` trägt entweder einen
 * Routinen-Slug, 'client-finding' oder 'manuell:<Benutzer>'.
 */
export function keyBelegQuelle(verifiedBy: string | null | undefined): string {
  const roh = (verifiedBy ?? '').trim();
  if (!roh) return 'Quelle unbekannt';
  if (roh.startsWith('manuell:')) return `von Hand eingetragen (${roh.slice(8)})`;
  if (roh === 'client-finding') return 'der Client hat sich damit angemeldet';
  return `Lauf der Routine ${roh}`;
}
